<template>
	<section class="vip-dashboard" :aria-busy="isLoading ? 'true' : 'false'">
		<div class="vip-profile-summary">
			<div class="vip-avatar" :class="{ 'is-vip-zero': memberLevel === 0 }">
				<span v-if="memberLevel === 0" class="vip-avatar-zero" aria-hidden="true">VIP0</span>
				<img v-else-if="memberIconImage" :src="memberIconImage" :alt="memberDisplayName"
					@error="useFallbackMemberIcon = true">
				<i v-else class="el-icon-user-solid" aria-hidden="true"></i>
			</div>
			<div class="vip-profile-main">
				<div class="vip-identity-row">
					<strong class="vip-level-name" :title="memberLevelName">
						<template v-if="levelNameParts.isVip">
							<span class="vip-level-mark" aria-hidden="true"><span>VIP</span></span>
							<span class="vip-level-number">{{ levelNameParts.suffix }}</span>
						</template>
						<span v-else class="vip-level-text">{{ memberLevelName }}</span>
					</strong>
				</div>
				<div class="vip-progress-line">
					<div class="vip-profile-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100"
						:aria-valuenow="memberProgressPercent">
						<span :style="{ width: memberProgressDisplayPercent + '%' }"></span>
					</div>
					<span v-if="isMaxLevel" class="vip-max-level-label">
						<i class="el-icon-trophy" aria-hidden="true"></i>
						{{ $t('personalCenter.vip.max_level') }}
					</span>
					<span v-else class="vip-next-level-label">{{ $t('personalCenter.vip.next_level', {
						level: memberNextLevel
					}) }}</span>
				</div>
				<p v-if="isMaxLevel" class="vip-progress-summary">{{ $t('personalCenter.vip.current_experience', {
					current: formatMemberPoints(memberCurrentPoints)
				}) }}</p>
				<p v-else class="vip-progress-summary">{{ $t('personalCenter.vip.experience_summary', {
					current: formatMemberPoints(memberCurrentPoints),
					level: memberNextLevel,
					remaining: formatMemberPoints(memberRemainingPoints)
				}) }}</p>
			</div>
			<div class="vip-profile-points">
				<p>{{ $t('personalCenter.vip.points_label') }}
					<strong>{{ isLoadingPointsBalance ? '--' : formatMemberPoints(pointsBalance) }}</strong>
				</p>
				<button type="button" @click="goToPointsMall">{{ $t('personalCenter.vip.go_redeem') }}</button>
			</div>
		</div>

		<section class="vip-reward-section">
			<div class="vip-reward-heading">
				<button type="button" class="vip-tier-arrow" :disabled="selectedTierIndex === 0"
					:aria-label="$t('personalCenter.vip.previous_level')" @click="selectedTierIndex--">
					<i class="el-icon-arrow-left" aria-hidden="true"></i>
				</button>
				<div class="vip-reward-copy">
					<span>{{ $t('personalCenter.vip.level_rewards') }}</span>
					<div class="vip-reward-title-row">
						<strong class="vip-level-name" :title="selectedTier.levelName"
							:aria-label="selectedTier.levelName">
							<template v-if="selectedTierNameParts.isVip">
								<span class="vip-level-mark" aria-hidden="true"><span>VIP</span></span>
								<span class="vip-level-number">{{ selectedTierNameParts.suffix }}</span>
							</template>
							<span v-else class="vip-level-text">{{ selectedTier.levelName }}</span>
						</strong>
						<h3>{{ $t('personalCenter.vip.exclusive_rewards') }}</h3>
					</div>
				</div>
				<button type="button" class="vip-tier-arrow" :disabled="selectedTierIndex === tiers.length - 1"
					:aria-label="$t('personalCenter.vip.next_level_button')" @click="selectedTierIndex++">
					<i class="el-icon-arrow-right" aria-hidden="true"></i>
				</button>
			</div>

			<div v-if="selectedTier.rewards.length" class="vip-reward-grid">
				<article v-for="(reward, index) in selectedTier.rewards" :key="reward.id || reward.key || index"
					class="vip-reward-item">
					<div class="vip-reward-icon">
						<img v-if="reward.icon" :src="reward.icon" alt="">
						<i v-else class="el-icon-present" aria-hidden="true"></i>
					</div>
					<strong>{{ getRewardLabel(reward) }}</strong>
					<span>{{ reward.description || $t('personalCenter.vip.promotion_reward', { level: selectedTier.level }) }}</span>
				</article>
			</div>
			<p v-else class="vip-reward-empty">{{ $t('personalCenter.no_content') }}</p>

			<template v-if="selectedTier.level > 0">
				<p class="vip-reward-status" :class="{ 'is-unlocked': isSelectedTierUnlocked }">
					<i :class="isSelectedTierUnlocked ? 'el-icon-circle-check' : 'el-icon-lock'" aria-hidden="true"></i>
					{{ $t(isSelectedTierUnlocked ? 'personalCenter.vip.unlocked' : 'personalCenter.vip.locked', {
						level: selectedTier.level
					}) }}
				</p>
				<button type="button" class="vip-claim-button"
					:disabled="!isSelectedTierUnlocked || isSelectedTierClaimed || isClaimingReward"
					:aria-busy="isClaimingReward ? 'true' : 'false'" @click="claimReward">
					<i v-if="isClaimingReward" class="el-icon-loading" aria-hidden="true"></i>
					{{ $t(isClaimingReward ? 'personalCenter.vip.claiming' :
						(isSelectedTierClaimed ? 'personalCenter.vip.claimed' : 'personalCenter.vip.claim')) }}
				</button>
			</template>
		</section>
	</section>
</template>

<script>
import dragonKeyImage from '../../assets/image/first.png'
import battleShardImage from '../../assets/image/recharge3.png'
import forgeCrystalImage from '../../assets/image/recharge1.png'

export default {
	name: 'VipMemberOverview',
	props: {
		gameData: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			localMemberData: null,
			isLoading: false,
			loadError: null,
			pointsBalance: 0,
			isLoadingPointsBalance: false,
			isClaimingReward: false,
			useFallbackMemberIcon: false,
			selectedTierIndex: 0,
			claimedLevels: []
		}
	},
	computed: {
		resolvedMemberData() {
			return this.localMemberData || {}
		},
		memberProfile() {
			return this.resolvedMemberData
		},
		memberDisplayName() {
			const roleInfo = this.$cache.getRoleInfo() || {}
			return roleInfo.roleName || this.memberProfile.roleName || this.memberProfile.nickname ||
				this.gameData.gameName || String(this.$session.getUserId() || '-')
		},
		memberAvatar() {
			const roleInfo = this.$cache.getRoleInfo() || {}
			return roleInfo.avatar || roleInfo.avatarUrl || roleInfo.headImage || this.memberProfile.avatar ||
				this.memberProfile.avatarUrl || this.memberProfile.headImage || this.gameData.gameIcon || ''
		},
		memberIconImage() {
			if (!this.useFallbackMemberIcon && this.resolvedMemberData.iconUrl) {
				return this.resolvedMemberData.iconUrl
			}
			return this.memberAvatar
		},
		memberLevel() {
			return Math.max(0, Number(this.resolvedMemberData.currentVipLevel) || 0)
		},
		isMaxLevel() {
			return this.localMemberData !== null && this.resolvedMemberData.nextLevel === null
		},
		memberLevelName() {
			return this.resolvedMemberData.levelName || `VIP${this.memberLevel}`
		},
		levelNameParts() {
			const name = String(this.memberLevelName || '').trim()
			const matched = /^VIP\s*(.*)$/i.exec(name)
			return matched
				? { isVip: true, suffix: matched[1] || String(this.memberLevel) }
				: { isVip: false, suffix: '' }
		},
		memberNextLevel() {
			const nextLevel = this.resolvedMemberData.nextLevel || {}
			return Math.max(this.memberLevel,
				Number(nextLevel.levelNo) || this.memberLevel + 1)
		},
		memberCurrentPoints() {
			return Math.max(0, Number(this.resolvedMemberData.totalExp) || 0)
		},
		memberRemainingPoints() {
			const target = Number(this.resolvedMemberData.nextLevel && this.resolvedMemberData.nextLevel.minExp)
			return target > 0 ? Math.max(0, target - this.memberCurrentPoints) : 0
		},
		memberProgressPercent() {
			if (this.isMaxLevel) return 100
			const target = Number(this.resolvedMemberData.nextLevel && this.resolvedMemberData.nextLevel.minExp)
			return target > 0 ? Math.min(100, Math.max(0, this.memberCurrentPoints / target * 100)) : 0
		},
		memberProgressDisplayPercent() {
			return this.memberProgressPercent > 0 ? Math.max(2, this.memberProgressPercent) : 0
		},
		tiers() {
			const fallbackIcons = [dragonKeyImage, battleShardImage, forgeCrystalImage]
			const rewardResults = Array.isArray(this.resolvedMemberData.rewardResults)
				? this.resolvedMemberData.rewardResults
				: []
			const fallbackTiers = [{
				level: this.memberLevel > 0 ? this.memberLevel : 1,
				levelName: `VIP${this.memberLevel > 0 ? this.memberLevel : 1}`,
				unlocked: this.memberLevel > 0,
				claimed: false,
				rewards: []
			}]
			if (!rewardResults.length) return fallbackTiers
			return rewardResults.map(tier => {
				const level = Math.max(0, Number(tier.levelNo) || 0)
				return {
					level,
					levelName: tier.levelName || `VIP${level}`,
					unlocked: level <= this.memberLevel,
					claimed: Boolean(tier.claimed),
					rewards: (tier.rewards || []).map((reward, index) => ({
						id: reward.rewardId || `${level}-${index}`,
						name: reward.rewardName || '',
						count: Math.max(0, Number(reward.quantity) || 0),
						icon: reward.iconUrl || fallbackIcons[index] || '',
						description: reward.description || ''
					}))
				}
			}).sort((left, right) => left.level - right.level)
		},
		selectedTier() {
			return this.tiers[this.selectedTierIndex] || this.tiers[0]
		},
		selectedTierNameParts() {
			const name = String(this.selectedTier.levelName || '').trim()
			const matched = /^VIP\s*(.*)$/i.exec(name)
			return matched
				? { isVip: true, suffix: matched[1] || String(this.selectedTier.level) }
				: { isVip: false, suffix: '' }
		},
		isSelectedTierUnlocked() {
			return this.selectedTier.unlocked !== undefined
				? this.selectedTier.unlocked
				: this.selectedTier.level <= this.memberLevel
		},
		isSelectedTierClaimed() {
			return this.selectedTier.claimed || this.claimedLevels.includes(this.selectedTier.level)
		}
	},
	watch: {
		resolvedMemberData: {
			immediate: true,
				handler() {
				this.claimedLevels = []
				this.$nextTick(() => {
					const defaultRewardLevel = this.memberLevel > 0 ? this.memberLevel : 1
					const defaultTierIndex = this.tiers.findIndex(tier => Number(tier.level) === defaultRewardLevel)
					this.selectedTierIndex = defaultTierIndex >= 0 ? defaultTierIndex : 0
				})
			}
		}
	},
	mounted() {
		this.loadMemberData()
		this.loadPointsBalance()
	},
	methods: {
		async loadMemberData() {
			const userId = this.$session.getUserId()
			const gameCode = this.gameData.gameCode || this.$cache.getGameCode()
			if (!userId || !gameCode) return

			this.isLoading = true
			this.loadError = null
			try {
				const response = await this.$axios({
					method: 'get',
					url: '/web/api/member/initData',
					silentError: true,
					params: { userId, gameCode }
				})
				if (!response || response.code !== 1000 || !response.data) {
					this.loadError = response || { message: 'Failed to load member data' }
					this.$emit('load-error', this.loadError)
					return
				}

				this.useFallbackMemberIcon = false
				this.localMemberData = response.data
				this.$emit('loaded', response.data)
			} catch (error) {
				this.loadError = error
				this.$emit('load-error', error)
			} finally {
				this.isLoading = false
			}
		},
		async loadPointsBalance() {
			const userId = this.$session.getUserId()
			const gameCode = this.gameData.gameCode || this.$cache.getGameCode()
			if (!userId || !gameCode) return

			this.isLoadingPointsBalance = true
			try {
				const response = await this.$axios({
					method: 'get',
					url: '/web/api/points/initData',
					silentError: true,
					params: { userId, gameCode }
				})
				if (response && response.code === 1000 && response.data) {
					this.pointsBalance = Math.max(0, Number(response.data.balance) || 0)
				}
			} catch (error) {
				this.$emit('points-load-error', error)
			} finally {
				this.isLoadingPointsBalance = false
			}
		},
		goToPointsMall() {
			if (this.$route.name !== 'points') this.$router.push({ name: 'points' })
		},
		formatMemberPoints(value) {
			if (value === null || value === undefined || value === '') return '--'
			const number = Number(value)
			if (!Number.isFinite(number)) return String(value)
			return new Intl.NumberFormat(String(this.$i18n.locale || 'en_US').replace('_', '-')).format(number)
		},
		getRewardLabel(reward) {
			if (reward.name) return reward.count > 0 ? `${reward.name} ×${reward.count}` : reward.name
			const key = `personalCenter.vip.${reward.key}`
			if (this.$te(key)) return this.$t(key, { count: reward.count })
			return reward.count > 0 ? `×${reward.count}` : ''
		},
		async claimReward() {
			if (!this.isSelectedTierUnlocked || this.isSelectedTierClaimed || this.isClaimingReward) return

			const levelNo = Number(this.selectedTier.level)
			const userId = this.$session.getUserId()
			const gameCode = this.gameData.gameCode || this.$cache.getGameCode()
			const roleInfo = this.$cache.getRoleInfo() || {}
			const data = { userId, gameCode, levelNo }
			if (roleInfo.roleId) data.roleId = roleInfo.roleId
			if (roleInfo.serverCode) data.serverCode = roleInfo.serverCode

			this.isClaimingReward = true
			try {
				const response = await this.$axios({
					method: 'post',
					url: '/web/api/member/claimReward',
					silentError: true,
					params: data
				})

				if (!response || response.code !== 1000) {
					this.$toast.error(response && response.message
						? response.message
						: this.$t('personalCenter.vip.claim_failed'))
					return
				}

				const rewardResults = this.resolvedMemberData.rewardResults || []
				const claimedTier = rewardResults.find(tier => Number(tier.levelNo) === levelNo)
				if (claimedTier) this.$set(claimedTier, 'claimed', true)
				if (!this.claimedLevels.includes(levelNo)) this.claimedLevels.push(levelNo)
				this.$emit('claim', this.selectedTier)
				this.$toast.success(this.$t('personalCenter.vip.claim_success', { level: levelNo }))
			} catch (error) {
				this.$emit('claim-error', error)
				this.$toast.error(this.$t('personalCenter.vip.claim_failed'))
			} finally {
				this.isClaimingReward = false
			}
		}
	}
}
</script>

<style scoped>
.vip-dashboard {
	display: grid;
	gap: 18px;
}

.vip-profile-summary {
	display: grid;
	grid-template-columns: 86px minmax(0, 1fr) auto;
	align-items: center;
	gap: 22px;
	box-sizing: border-box;
	min-height: 142px;
	border: 1px solid #e6e8eb;
	border-radius: 8px;
	padding: 20px 24px;
	background: #fff;
	box-shadow: 0 7px 20px rgba(31, 38, 47, .08);
}

.vip-avatar {
	display: grid;
	width: 82px;
	height: 82px;
	place-items: center;
	box-sizing: border-box;
	overflow: hidden;
	border: 3px solid #fff;
	border-radius: 50%;
	background: #f0f2f5;
	box-shadow: 0 0 0 2px rgba(var(--theme-color-rgb), .72), 0 5px 14px rgba(31, 38, 47, .12);
}

.vip-avatar img {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.vip-avatar.is-vip-zero {
	border-color: #fff1bd;
	color: #8b5310;
	background: #ffd96f;
	box-shadow: 0 0 0 1px #e7af38, 0 5px 14px rgba(91, 58, 18, .14);
}

.vip-avatar-zero {
	font-size: 17px;
	font-weight: 800;
}

.vip-avatar i { color: #8a9099; font-size: 38px; }
.vip-profile-main { min-width: 0; }

.vip-identity-row {
	display: flex;
	min-width: 0;
	align-items: center;
	gap: 14px;
}

.vip-identity-row h2 {
	min-width: 0;
	margin: 0;
	overflow: hidden;
	color: #22262c;
	font-size: 22px;
	font-weight: 800;
	line-height: 30px;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.vip-level-name {
	display: inline-flex;
	min-width: 0;
	max-width: 160px;
	align-items: center;
	gap: 5px;
	overflow: hidden;
	white-space: nowrap;
}

.vip-level-mark {
	position: relative;
	display: grid;
	width: 56px;
	height: 34px;
	flex: 0 0 56px;
	place-items: center;
	filter: drop-shadow(0 2px 2px rgba(121, 54, 12, .2));
}

.vip-level-mark::before,
.vip-level-mark::after {
	position: absolute;
	content: '';
	clip-path: polygon(50% 0, 62% 13%, 78% 7%, 82% 25%, 100% 31%, 87% 49%, 92% 68%, 68% 70%, 50% 100%, 32% 70%, 8% 68%, 13% 49%, 0 31%, 18% 25%, 22% 7%, 38% 13%);
}

.vip-level-mark::before {
	inset: 0;
	background: #f4b62f;
}

.vip-level-mark::after {
	inset: 3px 4px 4px;
	background: #e6461f;
}

.vip-level-mark span {
	position: relative;
	z-index: 1;
	color: #fff5c6;
	font-family: Georgia, 'Times New Roman', serif;
	font-size: 21px;
	font-style: italic;
	font-weight: 900;
	line-height: 1;
	text-shadow: 0 1px 0 #8e2712, 0 0 2px rgba(255, 241, 164, .8);
}

.vip-level-number {
	position: relative;
	top: -5px;
	color: #df4a21;
	background: linear-gradient(180deg, #ffb54e 0%, #f9782c 45%, #df3d1d 100%);
	-webkit-background-clip: text;
	background-clip: text;
	-webkit-text-fill-color: transparent;
	font-family: Georgia, 'Times New Roman', serif;
	font-size: 32px;
	font-weight: 900;
	line-height: 34px;
}

.vip-level-text {
	display: inline-block;
	max-width: 100%;
	overflow: hidden;
	color: var(--theme-color, #d26a2c);
	background: linear-gradient(180deg, #f7ad4a 0%, #e96927 55%, #c83c1b 100%);
	-webkit-background-clip: text;
	background-clip: text;
	-webkit-text-fill-color: transparent;
	padding: 0 2px 2px;
	font-size: 21px;
	font-weight: 900;
	line-height: 28px;
	text-overflow: ellipsis;
}

.vip-profile-progress {
	height: 8px;
	width: 100%;
	overflow: hidden;
	border-radius: 4px;
	background: #e5e7e8;
}

.vip-profile-progress span {
	display: block;
	height: 100%;
	border-radius: inherit;
	background: var(--theme-color, #f6aa50);
}

.vip-progress-line {
	display: grid;
	grid-template-columns: minmax(72px, 1fr) auto;
	max-width: 580px;
	align-items: center;
	gap: 12px;
}

.vip-next-level-label {
	display: inline-flex;
	min-height: 30px;
	align-items: center;
	box-sizing: border-box;
	border: 1px solid rgba(var(--theme-color-rgb), .32);
	border-radius: 6px;
	padding: 0 10px;
	color: var(--theme-color);
	background: rgba(var(--theme-color-rgb), .075);
	font-size: 13px;
	font-weight: 800;
	line-height: 18px;
	white-space: nowrap;
}

.vip-max-level-label {
	display: inline-flex;
	max-width: 180px;
	align-items: center;
	gap: 6px;
	color: #9b621d;
	font-size: 13px;
	font-weight: 800;
	line-height: 20px;
	text-align: left;
	overflow-wrap: anywhere;
}

.vip-max-level-label i { flex: 0 0 auto; color: #dda037; font-size: 17px; }

.vip-progress-summary {
	max-width: 560px;
	margin: 9px 0 0;
	border-left: 3px solid rgba(var(--theme-color-rgb), .72);
	padding-left: 9px;
	color: #656c76;
	font-size: 12px;
	font-weight: 600;
	line-height: 18px;
	overflow-wrap: anywhere;
}

.vip-profile-points {
	display: grid;
	justify-items: end;
	gap: 14px;
}

.vip-profile-points p {
	margin: 0;
	color: #686f79;
	font-size: 13px;
	line-height: 24px;
}

.vip-profile-points strong {
	margin-left: 5px;
	color: #262a31;
	font-size: 22px;
	font-weight: 900;
}

.vip-profile-points button {
	min-width: 92px;
	height: 38px;
	border: 0;
	border-radius: 19px;
	padding: 0 16px;
	color: #fff;
	background: var(--theme-color);
	font-size: 13px;
	font-weight: 800;
	cursor: pointer;
}

.vip-profile-points button:hover { box-shadow: 0 5px 12px rgba(var(--theme-color-rgb), .22); }
.vip-profile-points button:focus-visible { outline: 2px solid rgba(var(--theme-color-rgb), .45); outline-offset: 2px; }

.vip-reward-section {
	box-sizing: border-box;
	border: 1px solid #e3e6ea;
	border-radius: 8px;
	padding: 18px;
	background: #fff;
	box-shadow: 0 6px 18px rgba(31, 38, 47, .06);
}

.vip-reward-heading {
	display: grid;
	grid-template-columns: 38px minmax(0, 1fr) 38px;
	align-items: center;
	gap: 14px;
	padding: 0 0 15px;
	border-bottom: 1px solid #eceef1;
}

.vip-reward-copy { min-width: 0; text-align: center; }
.vip-reward-copy > span { color: #77736d; font-size: 12px; line-height: 18px; }

.vip-reward-title-row {
	display: flex;
	min-width: 0;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-top: 2px;
}

.vip-reward-copy h3 { margin: 0; color: #332d29; font-size: 18px; font-weight: 800; line-height: 25px; }

.vip-tier-arrow {
	display: grid;
	width: 38px;
	height: 38px;
	place-items: center;
	border: 1px solid #e0e3e7;
	border-radius: 6px;
	padding: 0;
	color: var(--theme-color);
	background: #fafbfc;
	font-size: 17px;
	cursor: pointer;
}

.vip-tier-arrow:hover:not(:disabled) { border-color: rgba(var(--theme-color-rgb), .5); }
.vip-tier-arrow:focus-visible, .vip-claim-button:focus-visible { outline: 2px solid rgba(var(--theme-color-rgb), .45); outline-offset: 2px; }
.vip-tier-arrow:disabled { color: #b6bac0; background: #f3f4f5; cursor: not-allowed; }

.vip-reward-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 0;
	margin-top: 14px;
	overflow: hidden;
	border: 1px solid #e4e7eb;
	border-radius: 8px;
	background: #fafbfc;
}

.vip-reward-item {
	display: flex;
	min-width: 0;
	min-height: 142px;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	box-sizing: border-box;
	border: 0;
	border-right: 1px solid #e4e7eb;
	padding: 16px 12px;
	background: transparent;
	text-align: center;
	transition: background-color .16s ease;
}

.vip-reward-item:last-child { border-right: 0; }
.vip-reward-item:hover { background: #fff; }

.vip-reward-icon {
	display: grid;
	width: 56px;
	height: 56px;
	place-items: center;
	margin-bottom: 10px;
	border: 1px solid rgba(var(--theme-color-rgb), .14);
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 3px 10px rgba(31, 38, 47, .05);
}

.vip-reward-icon img { display: block; width: 44px; height: 44px; object-fit: contain; }
.vip-reward-icon i { color: var(--theme-color); font-size: 24px; }
.vip-reward-item strong { color: #302d2a; font-size: 13px; font-weight: 800; line-height: 19px; overflow-wrap: anywhere; }
.vip-reward-item > span { margin-top: 4px; color: #9a6a39; font-size: 11px; line-height: 17px; overflow-wrap: anywhere; }

.vip-reward-empty {
	display: grid;
	min-height: 128px;
	place-items: center;
	margin: 0;
	border: 1px dashed #dadde1;
	border-radius: 8px;
	color: #858b94;
	background: #fff;
	font-size: 13px;
}

.vip-reward-status {
	display: flex;
	min-height: 36px;
	align-items: center;
	justify-content: center;
	gap: 6px;
	box-sizing: border-box;
	margin: 14px 0 10px;
	border-radius: 6px;
	padding: 7px 12px;
	color: #686f79;
	background: #f4f5f6;
	font-size: 12px;
	line-height: 18px;
	text-align: center;
}

.vip-reward-status.is-unlocked { color: #277555; background: #eef8f3; }
.vip-reward-status i { flex: 0 0 auto; font-size: 15px; }

.vip-claim-button {
	display: flex;
	width: 100%;
	height: 42px;
	align-items: center;
	justify-content: center;
	gap: 7px;
	border: 1px solid var(--theme-color);
	border-radius: 6px;
	color: #fff;
	background: var(--theme-color);
	font-size: 14px;
	font-weight: 800;
	cursor: pointer;
}

.vip-claim-button:hover:not(:disabled) { box-shadow: 0 5px 12px rgba(var(--theme-color-rgb), .2); }
.vip-claim-button:disabled { border-color: #d8dadd; color: #9ca1a8; background: #eceef0; cursor: not-allowed; }

@media (prefers-reduced-motion: reduce) {
	.vip-tier-arrow, .vip-claim-button { transition: none; }
}

@media (max-width: 767px) {
	.vip-dashboard { gap: 12px; }
	.vip-profile-summary { grid-template-columns: 58px minmax(0, 1fr); gap: 14px; min-height: 0; padding: 18px 16px; }
	.vip-avatar { width: 56px; height: 56px; }
	.vip-avatar-zero { font-size: 13px; }
	.vip-avatar i { font-size: 27px; }
	.vip-identity-row { gap: 8px; }
	.vip-identity-row h2 { font-size: 17px; line-height: 24px; }
	.vip-level-name { max-width: 110px; }
	.vip-level-mark { width: 44px; height: 27px; flex-basis: 44px; }
	.vip-level-mark span { font-size: 16px; }
	.vip-level-number { font-size: 26px; line-height: 27px; }
	.vip-level-text { font-size: 16px; line-height: 22px; }
	.vip-progress-line { gap: 8px; margin-top: 10px; }
	.vip-next-level-label { min-height: 28px; padding: 0 8px; font-size: 11px; }
	.vip-max-level-label { max-width: 140px; gap: 4px; font-size: 11px; line-height: 16px; }
	.vip-max-level-label i { font-size: 14px; }
	.vip-progress-summary { margin-top: 8px; font-size: 11px; line-height: 17px; }
	.vip-profile-points { grid-column: 1 / -1; grid-template-columns: minmax(0, 1fr) auto; align-items: center; justify-items: start; gap: 12px; }
	.vip-profile-points button { min-width: 86px; }
	.vip-reward-section { padding: 12px; }
	.vip-reward-heading { grid-template-columns: 36px minmax(0, 1fr) 36px; gap: 8px; }
	.vip-tier-arrow { width: 36px; height: 36px; }
	.vip-reward-title-row { gap: 5px; }
	.vip-reward-grid { grid-template-columns: 1fr; }
	.vip-reward-item { border-right: 0; border-bottom: 1px solid #e4e7eb; }
	.vip-reward-item:last-child { border-bottom: 0; }
	.vip-reward-item { min-height: 112px; }
}
</style>
